// src/mileage.js
import { useCallback, useEffect, useRef, useState } from 'react';
import { getApiBase } from './apiBase';
import { getTenantHeaders } from './api';
import SavedToastOverlay from './components/SavedToastOverlay';
import TitleBar from './components/ui/TitleBar';
import BackToDashboardButton from './components/ui/BackToDashboardButton';
import AddressAutocomplete from './AddressAutocomplete';
import OperationalListControls from './components/OperationalListControls';

const MILEAGE_LIST_PAGE_SIZE = 50;
const MILEAGE_LIST_TIMEOUT_MS = 15000;

const isValidMileageListResponse = (data, includeYears = false) => {
  const pagination = data?.pagination;
  return Boolean(
    data &&
    Array.isArray(data.rows) &&
    pagination &&
    Number.isInteger(pagination.page) && pagination.page >= 1 &&
    Number.isInteger(pagination.page_size) && pagination.page_size >= 1 &&
    Number.isInteger(pagination.total) && pagination.total >= 0 &&
    Number.isInteger(pagination.total_pages) && pagination.total_pages >= 1 &&
    (!includeYears || (
      Array.isArray(data.available_years) &&
      data.available_years.every((year) => Number.isInteger(year))
    ))
  );
};

const Mileage = () => {
  const [trips, setTrips] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [formData, setFormData] = useState({
    id: null,
    date: new Date().toISOString().split('T')[0],
    purpose: '',
    startAddress: '',
    endAddress: '',
    distance: '',
    notes: '',
    startOdometer: '',
    endOdometer: '',
    vehicle_id: '',
    returnOfMileageId: null
  });

  const [mileageListYear, setMileageListYear] = useState(new Date().getFullYear());
  const [mileageListSearchDraft, setMileageListSearchDraft] = useState('');
  const [mileageListSearch, setMileageListSearch] = useState('');
  const [mileageListPage, setMileageListPage] = useState(1);
  const [mileageListPagination, setMileageListPagination] = useState({
    page: 1,
    page_size: MILEAGE_LIST_PAGE_SIZE,
    total: 0,
    total_pages: 1
  });
  const [availableMileageYears, setAvailableMileageYears] = useState([]);
  const [mileageListError, setMileageListError] = useState('');
  const [mileageListVersion, setMileageListVersion] = useState(0);
  const mileageListRequestRef = useRef(0);
  const mileageYearsNeededRef = useRef(true);
  const [confirmation, setConfirmation] = useState('');
  const [distanceLoading, setDistanceLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  const API_BASE = getApiBase();
  const activeVehicles = vehicles.filter(vehicle => Number(vehicle.is_active) === 1);

  const vehicleLabel = (vehicle) => {
    if (!vehicle) return 'UNASSIGNED / HISTORICAL';
    const details = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ');
    return `UNIT # ${vehicle.unit_number}${details ? ` — ${details}` : ''}`;
  };

  const tripVehicleLabel = (trip) => {
    if (trip.vehicle_id == null) return 'UNASSIGNED / HISTORICAL';
    return vehicleLabel({
      unit_number: trip.vehicle_unit_number,
      year: trip.vehicle_year,
      make: trip.vehicle_make,
      model: trip.vehicle_model
    });
  };

  const loadMileage = useCallback(async ({ signal, includeYears, requestId, didTimeout }) => {
    const query = new URLSearchParams({
      page: String(mileageListPage),
      page_size: String(MILEAGE_LIST_PAGE_SIZE)
    });

    if (mileageListSearch.trim()) {
      query.set('search', mileageListSearch);
    } else {
      query.set('year', String(mileageListYear));
    }
    if (includeYears) query.set('include_years', '1');

    try {
      setMileageListError('');
      const res = await fetch(`${API_BASE}/api/mileage?${query.toString()}`, {
        headers: { ...getTenantHeaders() },
        signal
      });
      if (!res.ok) throw new Error(`Mileage list request failed (${res.status}).`);

      const data = await res.json();
      if (!isValidMileageListResponse(data, includeYears)) {
        throw new Error('Mileage list returned an invalid response.');
      }

      if (requestId !== mileageListRequestRef.current) return;
      setTrips(data.rows);
      setMileageListPagination(data.pagination);
      if (includeYears) {
        setAvailableMileageYears(data.available_years);
        mileageYearsNeededRef.current = false;
      }
    } catch (error) {
      if (requestId !== mileageListRequestRef.current) return;
      if (error.name === 'AbortError' && !didTimeout()) return;
      const message = didTimeout()
        ? 'Mileage list request timed out. Please try again.'
        : (error?.message || 'Failed to load mileage.');
      console.error('Failed to fetch mileage:', error);
      setMileageListError(message);
    }
  }, [API_BASE, mileageListPage, mileageListSearch, mileageListYear]);

  const refreshMileageList = useCallback(({ includeYears = false } = {}) => {
    if (includeYears) mileageYearsNeededRef.current = true;
    setMileageListVersion((version) => version + 1);
  }, []);

  const fetchVehicles = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/settings/vehicles`, {
        headers: { ...getTenantHeaders() }
      });
      if (!res.ok) return [];
      const data = await res.json();
      const arr = Array.isArray(data) ? data : [];
      setVehicles(arr);
      return arr;
    } catch (e) {
      console.error('Failed to fetch vehicles:', e);
      return [];
    }
  };

  useEffect(() => {
    fetchVehicles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!mileageListSearchDraft.trim()) {
      setMileageListSearch('');
      setMileageListPage(1);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setMileageListSearch(mileageListSearchDraft.trim());
      setMileageListPage(1);
    }, 400);

    return () => window.clearTimeout(timer);
  }, [mileageListSearchDraft]);

  useEffect(() => {
    const controller = new AbortController();
    const requestId = mileageListRequestRef.current + 1;
    mileageListRequestRef.current = requestId;
    const includeYears = mileageYearsNeededRef.current;
    let timedOut = false;
    const timeout = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, MILEAGE_LIST_TIMEOUT_MS);

    loadMileage({
      signal: controller.signal,
      includeYears,
      requestId,
      didTimeout: () => timedOut
    }).finally(() => window.clearTimeout(timeout));

    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [loadMileage, mileageListVersion]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const start = parseFloat(formData.startOdometer);
    const end   = parseFloat(formData.endOdometer);
    if (!isNaN(start) && !isNaN(end) && end > start) {
      setFormData(prev => ({ ...prev, distance: (end - start).toFixed(1) }));
    }
  }, [formData.startOdometer, formData.endOdometer]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const calculateDistance = async () => {
    if (distanceLoading) return;
    if (!formData.startAddress || !formData.endAddress) {
      alert('Please enter both Start and End addresses first!');
      return;
    }
    setDistanceLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/mileage/distance?origin=${encodeURIComponent(formData.startAddress)}&destination=${encodeURIComponent(formData.endAddress)}`,
        { headers: { ...getTenantHeaders() } }
      );
      const data = await res.json();
      if (data.ok && data.miles != null) {
        setFormData(prev => ({ ...prev, distance: String(data.miles) }));
        window.dispatchEvent(new CustomEvent('jobflow:saved', {
          detail: { message: 'DISTANCE SET!' }
        }));
      } else {
        alert(data.error || 'Could not calculate distance. Please enter miles manually.');
      }
    } catch (_e) {
      alert('Distance calculation failed. Please enter miles manually.');
    } finally {
      setDistanceLoading(false);
    }
  };

  const canReverseTrip = (trip) => (
    Number(trip.is_return_trip || 0) !== 1 &&
    Number(trip.has_return_trip || 0) !== 1 &&
    Boolean(String(trip.startAddress || '').trim()) &&
    Boolean(String(trip.endAddress || '').trim()) &&
    (trip.startOdometer == null || String(trip.startOdometer).trim() === '') &&
    (trip.endOdometer == null || String(trip.endOdometer).trim() === '')
  );

  const reverseTrip = (trip) => {
    if (!canReverseTrip(trip)) return;
    setFormData({
      id: null,
      date: trip.date ? trip.date.split('T')[0] : new Date().toISOString().split('T')[0],
      purpose: trip.purpose || '',
      startAddress: trip.endAddress,
      endAddress: trip.startAddress,
      distance: trip.distance != null ? String(trip.distance) : '',
      notes: trip.notes || '',
      startOdometer: '',
      endOdometer: '',
      vehicle_id: trip.vehicle_id != null ? String(trip.vehicle_id) : '',
      returnOfMileageId: Number(trip.id)
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const oldId = formData.id ? Number(formData.id) : null;
    const isUpdate = Boolean(oldId);
    const isReturnTrip = formData.returnOfMileageId != null;

    if (!isUpdate && !isReturnTrip && activeVehicles.length >= 2 && !formData.vehicle_id) {
      alert('Please select a vehicle.');
      return;
    }

    const payload = {
      date: formData.date,
      purpose: formData.purpose,
      startAddress: formData.startAddress,
      endAddress: formData.endAddress,
      distance: Number(formData.distance || 0),
      notes: formData.notes || '',
      startOdometer: formData.startOdometer !== '' ? Number(formData.startOdometer) : null,
      endOdometer:   formData.endOdometer   !== '' ? Number(formData.endOdometer)   : null,
      returnOfMileageId: formData.returnOfMileageId
    };

    if (!isReturnTrip) {
      if (!isUpdate && activeVehicles.length === 1) {
        payload.vehicle_id = activeVehicles[0].id;
      } else if (formData.vehicle_id !== '') {
        payload.vehicle_id = Number(formData.vehicle_id);
      }
    }

    try {
      const res = await fetch(
        isUpdate ? `${API_BASE}/api/mileage/${oldId}` : `${API_BASE}/api/mileage`,
        {
          method: isUpdate ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json', ...getTenantHeaders() },
          body: JSON.stringify(payload)
        }
      );

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        if (res.status === 409 && formData.returnOfMileageId != null) {
          refreshMileageList({ includeYears: true });
        }
        throw new Error(errorData.error || 'Save failed');
      }

      window.dispatchEvent(new CustomEvent('jobflow:saved', {
        detail: { message: isUpdate ? 'TRIP UPDATED!' : 'TRIP LOGGED!' }
      }));

      refreshMileageList({ includeYears: true });

      setFormData({
        id: null,
        date: new Date().toISOString().split('T')[0],
        purpose: '',
        startAddress: '',
        endAddress: '',
        distance: '',
        notes: '',
        startOdometer: '',
        endOdometer: '',
        vehicle_id: '',
        returnOfMileageId: null
      });

      setTimeout(() => {
        setConfirmation('');
        const ledger = document.querySelector('[data-mileage-ledger]');
        if (ledger) ledger.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 1500);

    } catch (err) {
      alert('Failed to save trip: ' + (err?.message || 'Save failed'));
    }
  };

  const deleteTrip = async (id) => {
    if (!window.confirm("Delete this trip log?")) return;

    try {
      const res = await fetch(`${API_BASE}/api/mileage/${id}`, {
        method: 'DELETE',
        headers: { ...getTenantHeaders() }
      });

      if (!res.ok) throw new Error('Delete failed');

      mileageYearsNeededRef.current = true;
      if (trips.length === 1 && mileageListPage > 1) {
        setMileageListPage((page) => page - 1);
      } else {
        refreshMileageList();
      }
    } catch (err) {
      alert('Failed to delete trip: ' + err.message);
    }
  };

  const editTrip = (trip) => {
    setFormData({
      id: trip.id,
      date: trip.date ? trip.date.split('T')[0] : new Date().toISOString().split('T')[0],
      purpose: trip.purpose || '',
      startAddress: trip.startAddress || '',
      endAddress: trip.endAddress || '',
      distance: String(trip.distance || ''),
      notes: trip.notes || '',
      startOdometer: trip.startOdometer != null ? String(trip.startOdometer) : '',
      endOdometer:   trip.endOdometer   != null ? String(trip.endOdometer)   : '',
      vehicle_id: trip.vehicle_id != null ? String(trip.vehicle_id) : '',
      returnOfMileageId: trip.return_of_mileage_id != null
        ? Number(trip.return_of_mileage_id)
        : null
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const styles = {
    container: { padding: isMobile ? '15px' : '40px', maxWidth: '1400px' },
    form: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
      gap: '20px',
      backgroundColor: '#C9EBFF',
      padding: isMobile ? '15px' : '30px',
      border: '8px solid black',
      boxShadow: 'inset 0 0 0 3px #1e40af',
      marginBottom: '40px'
    },
    input: {
      padding: '15px',
      border: '4px solid black',
      fontWeight: '900',
      fontSize: '16px',
      backgroundColor: 'white',
      width: '100%',
      boxSizing: 'border-box'
    },
    label: { fontWeight: '900', fontSize: '13px', marginBottom: '8px', display: 'block', textTransform: 'uppercase' },
    button: {
      gridColumn: isMobile ? 'auto' : 'span 4',
      padding: '20px',
      backgroundColor: 'black',
      color: '#FFA500',
      fontWeight: '900',
      border: '4px solid #E8761D',
      cursor: 'pointer',
      textTransform: 'uppercase',
      fontSize: '20px'
    },
    mapsBtn: {
      gridColumn: isMobile ? 'auto' : 'span 4',
      padding: '15px',
      backgroundColor: '#4285F4',
      color: 'white',
      fontWeight: '900',
      border: '4px solid #E8761D',
      cursor: 'pointer',
      marginBottom: '5px',
      textTransform: 'uppercase',
      fontSize: '16px'
    },
    ledger: {
      display: 'grid',
      gap: '14px',
      width: '100%',
      border: '10px solid black',
      boxShadow: 'inset 0 0 0 3px #1e40af',
      padding: '12px',
      backgroundColor: '#C9EBFF',
      boxSizing: 'border-box'
    },
    operationalFrame: {
      border: '8px solid black',
      padding: '12px',
      backgroundColor: '#C9EBFF',
      marginBottom: '16px',
      boxSizing: 'border-box'
    },
    tripCard: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: isMobile ? 'stretch' : 'center',
      justifyContent: 'space-between',
      gap: isMobile ? '14px' : '24px',
      padding: isMobile ? '16px' : '18px 20px',
      border: '8px solid black',
      backgroundColor: 'white',
      boxSizing: 'border-box',
      width: '100%',
      minWidth: 0
    },
    tripInfo: { flex: '1 1 auto', minWidth: 0, overflowWrap: 'anywhere' },
    tripMeta: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: isMobile ? '6px 14px' : '8px 22px',
      marginBottom: '8px',
      fontWeight: 900
    },
    tripPurpose: { fontSize: '18px', fontWeight: 900, marginBottom: '6px' },
    tripDetail: { fontSize: '14px', fontWeight: 700, lineHeight: 1.45 },
    confirmBox: {
      gridColumn: isMobile ? 'auto' : 'span 4',
      backgroundColor: '#10b981',
      color: 'white',
      padding: '15px',
      fontWeight: '900',
      textAlign: 'center',
      fontSize: '18px',
      border: '4px solid black',
      marginBottom: '10px'
    },

    // ✅ Boxed action buttons (base)
    actBtn: {
      background: '#ffffff',
      color: 'black',
      border: '3px solid black',
      padding: '6px 10px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase'
    },

    // ✅ Fix stacking in ACTIONS column
    actionsCell: {
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? '6px' : '10px',
      flexWrap: 'wrap',
      justifyContent: isMobile ? 'flex-start' : 'flex-end',
      flex: '0 0 auto',
      maxWidth: '100%'
    }
  };

  return (
    <div className="jfn-mileage-page" style={styles.container}>
      <SavedToastOverlay accentColor="#60A5FA" />
      {/* Hover effects for relevant buttons */}
      <style>{`
        @media (max-width: 767px) {
          .jfn-mileage-page {
            padding-left: 6px !important;
            padding-right: 6px !important;
          }
        }
        .jfn-mile-btn {
          transition: transform 120ms ease, filter 120ms ease, background-color 120ms ease, color 120ms ease, border-color 120ms ease, box-shadow 120ms ease;
        }
        .jfn-mile-btn:hover {
          transform: translateY(-1px);
          filter: brightness(1.03);
        }
        /* Full-fill neon hover (not just border) */
        .jfn-mile-fill:hover {
          background-color: #FFC107 !important;
          color: #000 !important;
          border-color: #FFC107 !important;
          box-shadow: 0 0 10px rgba(255,193,7,0.75) !important;
        }
        .jfn-odo-input::-webkit-outer-spin-button,
        .jfn-odo-input::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        .jfn-odo-input {
          appearance: textfield;
          -moz-appearance: textfield;
        }
      `}</style>

      {/* TOP TITLE BAR (STANDARDIZED) */}
      <TitleBar
        icon="🚗"
        title="MILEAGE TRACKER"
        accent="#60a5fa"
        right={
          <BackToDashboardButton
            onClick={() => (window.location.href = '/dashboard')}
          />
        }
      />

      <form style={styles.form} onSubmit={handleSubmit}>
        {confirmation && <div style={styles.confirmBox}>{confirmation}</div>}

        <div style={{ gridColumn: isMobile ? 'auto' : 'span 1' }}>
          <label style={styles.label}>Date</label>
          <input style={styles.input} type="date" name="date" value={formData.date} onChange={handleInputChange} required />
        </div>

        <div style={{ gridColumn: isMobile ? 'auto' : 'span 3' }}>
          <label style={styles.label}>Trip Purpose</label>
          <input style={styles.input} name="purpose" value={formData.purpose} onChange={handleInputChange} placeholder="E.G. SERVICE CALL" required />
        </div>

        {formData.returnOfMileageId != null && (formData.vehicle_id !== '' || activeVehicles.length > 0) && (
          <div style={{ gridColumn: isMobile ? 'auto' : 'span 4' }}>
            <label style={styles.label}>Vehicle</label>
            <div style={styles.input}>
              {tripVehicleLabel(trips.find(trip => Number(trip.id) === Number(formData.returnOfMileageId)) || {})}
            </div>
          </div>
        )}

        {formData.returnOfMileageId == null && (
          (formData.id && activeVehicles.length >= 1) ||
          (!formData.id && activeVehicles.length >= 2)
        ) && (
          <div style={{ gridColumn: isMobile ? 'auto' : 'span 4' }}>
            <label style={styles.label}>Vehicle</label>
            <select
              style={styles.input}
              name="vehicle_id"
              value={formData.vehicle_id}
              onChange={handleInputChange}
              required={!formData.id}
            >
              <option value="">
                {formData.id ? 'UNASSIGNED / HISTORICAL' : 'SELECT VEHICLE'}
              </option>
              {formData.id && formData.vehicle_id !== '' && !activeVehicles.some(vehicle => String(vehicle.id) === String(formData.vehicle_id)) && (
                <option value={formData.vehicle_id}>
                  {`${tripVehicleLabel(trips.find(trip => Number(trip.id) === Number(formData.id)) || {})} — INACTIVE`}
                </option>
              )}
              {activeVehicles.map(vehicle => (
                <option key={vehicle.id} value={vehicle.id}>{vehicleLabel(vehicle)}</option>
              ))}
            </select>
          </div>
        )}

        <div style={{ gridColumn: isMobile ? 'auto' : 'span 2' }}>
          <label style={styles.label}>Start Address</label>
          <AddressAutocomplete
            inputStyle={styles.input}
            placeholder="STREET, CITY, ZIP"
            name="startAddress"
            value={formData.startAddress}
            onChange={(v) => setFormData(prev => ({ ...prev, startAddress: v }))}
            onAddressSelect={({ formatted, street, city, state, zip }) => {
              const addr = formatted || [street, city, state, zip].filter(Boolean).join(', ');
              setFormData(prev => ({ ...prev, startAddress: addr }));
            }}
          />
        </div>

        <div style={{ gridColumn: isMobile ? 'auto' : 'span 2' }}>
          <label style={styles.label}>End Address</label>
          <AddressAutocomplete
            inputStyle={styles.input}
            placeholder="STREET, CITY, ZIP"
            name="endAddress"
            value={formData.endAddress}
            onChange={(v) => setFormData(prev => ({ ...prev, endAddress: v }))}
            onAddressSelect={({ formatted, street, city, state, zip }) => {
              const addr = formatted || [street, city, state, zip].filter(Boolean).join(', ');
              setFormData(prev => ({ ...prev, endAddress: addr }));
            }}
          />
        </div>

        <div style={{ gridColumn: isMobile ? 'auto' : 'span 4', textAlign: 'center', padding: '4px 0' }}>
          <span style={{ fontWeight: 700, fontSize: '13px', textTransform: 'uppercase', color: '#FFA500', letterSpacing: '0', backgroundColor: 'black', padding: '5px 14px', display: 'inline-block' }}>
            Select Address Or Odometer to Log A Trip
          </span>
        </div>

        <div style={{ gridColumn: isMobile ? 'auto' : 'span 2' }}>
          <label style={styles.label}>Beginning Odometer</label>
          <input
            style={styles.input}
            type="number"
            name="startOdometer"
            className="jfn-odo-input"
            value={formData.startOdometer}
            onChange={handleInputChange}
            placeholder="00000.0"
            step="0.1"
            onWheel={(e) => e.currentTarget.blur()}
          />
        </div>

        <div style={{ gridColumn: isMobile ? 'auto' : 'span 2' }}>
          <label style={styles.label}>Ending Odometer</label>
          <input
            style={styles.input}
            type="number"
            name="endOdometer"
            className="jfn-odo-input"
            value={formData.endOdometer}
            onChange={handleInputChange}
            placeholder="00000.0"
            step="0.1"
            onWheel={(e) => e.currentTarget.blur()}
          />
        </div>

        <button
          type="button"
          onClick={calculateDistance}
          style={{ ...styles.mapsBtn, ...(distanceLoading ? { cursor: 'not-allowed' } : {}) }}
          className="jfn-mile-btn jfn-mile-fill"
        >
          {distanceLoading ? '⏳ Calculating...' : '🗺️ Get Mileage from Google Maps'}
        </button>

        <div style={{ gridColumn: isMobile ? 'auto' : 'span 1' }}>
          <label style={styles.label}>Total Miles</label>
          <input style={styles.input} type="number" name="distance" value={formData.distance} onChange={handleInputChange} placeholder="0.0" step="0.1" required />
        </div>

        <div style={{ gridColumn: isMobile ? 'auto' : 'span 3' }}>
          <label style={styles.label}>Trip Notes (Optional)</label>
          <input style={styles.input} name="notes" value={formData.notes} onChange={handleInputChange} placeholder="ADD SPECIFIC DETAILS ABOUT THE TRIP..." />
        </div>

        <button style={styles.button} type="submit" className="jfn-mile-btn jfn-mile-fill">
          {formData.id ? 'Update Trip Entry' : 'Lock Trip to Ledger'}
        </button>
      </form>

      {/* LOWER TITLE BAR (STANDARDIZED) */}
      <TitleBar
        icon="🔎"
        title={`TRIP LEDGER (${Number(mileageListPagination.total || 0)})`}
        accent="#1e40af"
        wrapStyle={{ marginBottom: '12px', background: '#C9EBFF' }}
      />

      <div style={styles.operationalFrame}>
        <OperationalListControls
          moduleName="Mileage"
          searchPlaceholder="Purpose, Date, Vehicle"
          showResultCount={false}
          theme={{
            background: '#C9EBFF',
            border: '#1e40af',
            accent: '#1e40af',
            text: 'black',
            inputBackground: 'white',
            inputText: 'black',
            icon: 'black',
            buttonBackground: '#1e40af',
            buttonText: 'white',
            buttonHover: '#1e40af',
            otherYearButtonBorder: 'black',
            otherYearButtonHover: '#FFC107',
            divider: '#1e40af',
            error: '#1e40af'
          }}
          selectedYear={mileageListYear}
          availableYears={availableMileageYears}
          search={mileageListSearchDraft}
          isSearchActive={Boolean(mileageListSearch.trim())}
          pagination={mileageListPagination}
          onYearChange={(year) => {
            setMileageListYear(year);
            setMileageListPage(1);
          }}
          onSearchChange={setMileageListSearchDraft}
          onPageChange={(page) => {
            if (page < 1 || page > Number(mileageListPagination.total_pages || 1)) return;
            setMileageListPage(page);
          }}
        />
      </div>

      {mileageListError ? (
        <div style={{ border: '4px solid #1e40af', background: 'white', color: 'black', padding: '12px', marginBottom: '12px', fontWeight: 900 }} role="alert">
          {mileageListError}
        </div>
      ) : null}

      <div style={styles.ledger} data-mileage-ledger>
        {trips.length === 0 ? (
          <div style={{ ...styles.tripCard, justifyContent: 'center', textAlign: 'center', fontWeight: 900 }}>
            {mileageListSearch.trim()
              ? 'NO MATCHING TRIPS FOUND'
              : `NO TRIPS FOUND FOR ${mileageListYear}`}
          </div>
        ) : (
          trips.map(t => {
            const hasAddressRoute = Boolean(t.startAddress || t.endAddress);
            const hasStartOdometer = t.startOdometer != null && String(t.startOdometer).trim() !== '';
            const hasEndOdometer = t.endOdometer != null && String(t.endOdometer).trim() !== '';
            const hasOdometerRoute = !hasAddressRoute && (hasStartOdometer || hasEndOdometer);

            return (
              <article key={t.id} style={styles.tripCard}>
                <div style={styles.tripInfo}>
                  <div style={styles.tripMeta}>
                    <span>{t.date ? new Date(t.date.slice(0, 10) + 'T00:00:00').toLocaleDateString() : 'N/A'}</span>
                    <span style={{ color: '#1e40af' }}>{Number(t.distance || 0).toFixed(1)} mi</span>
                    <span>{tripVehicleLabel(t)}</span>
                  </div>

                  <div style={styles.tripPurpose}>{t.purpose}</div>

                  {hasAddressRoute && (
                    <div style={styles.tripDetail}>
                      {t.startAddress && <div>FROM: {t.startAddress}</div>}
                      {t.endAddress && <div>TO: {t.endAddress}</div>}
                    </div>
                  )}

                  {hasOdometerRoute && (
                    <div style={styles.tripDetail}>
                      {hasStartOdometer && <div>BEGINNING ODOMETER: {Number(t.startOdometer).toLocaleString()}</div>}
                      {hasEndOdometer && <div>ENDING ODOMETER: {Number(t.endOdometer).toLocaleString()}</div>}
                    </div>
                  )}
                </div>

                <div style={styles.actionsCell}>
                  {canReverseTrip(t) && (
                    <button
                      type="button"
                      className="jfn-mile-btn jfn-mile-fill"
                      style={{ ...styles.actBtn, color: '#166534', whiteSpace: 'nowrap', flexShrink: 0 }}
                      onClick={() => reverseTrip(t)}
                    >
                      {isMobile ? '+ RET' : '+ RETURN'}
                    </button>
                  )}
                  <button
                    type="button"
                    className="jfn-mile-btn jfn-mile-fill"
                    style={{ ...styles.actBtn, color: 'blue' }}
                    onClick={() => editTrip(t)}
                  >
                    EDIT
                  </button>
                  <button
                    type="button"
                    className="jfn-mile-btn jfn-mile-fill"
                    style={{ ...styles.actBtn, color: 'red' }}
                    onClick={() => deleteTrip(t.id)}
                  >
                    DEL
                  </button>
                </div>
              </article>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Mileage;
